/**
 * @file OHIDLighting.ts
 * @author Rapoo Device SDK Team
 * @date 2026-06-05
 * @description OHID键盘灯光控制功能实现
 */
import { KeyboardLightingFeature } from '../../../../features/keyboard-lighting/contract'
import { RGB } from '../../../../types/color'

import { CMD_OHID, OHID_MIX_ORDER, OHID_CMD_WRITE, PROTOCOL_CONFIG } from '../../protocols/cmd'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { calculateChecksum } from '../../protocols/common'
import { LightingConfigData, LightingDomainType } from '../../../../types/lightingDomain'
import {
  LIGHTING_TARGET,
  type KeyboardLightingTarget,
  type LightingCapability,
} from '../../../../types/lighting'

import { KeyboardLightingEffect } from '../../../../types/keyboardLighting'

const MASTER_REPORT_ID = PROTOCOL_CONFIG.master_report_id
const REPORT_ID = PROTOCOL_CONFIG.report_id

export class OHIDKeyboardLighting extends KeyboardLightingFeature {
  private rgbToUint32(rgb: RGB, alpha: number = 0xff): number {
    return (
      (alpha << 24) | // Alpha 通道 (最高位)
      (rgb.r << 16) | // 蓝色通道
      (rgb.g << 8) | // 绿色通道
      (rgb.b << 0) // 红色通道 (最低位)
    )
  }

  /**
   * @param value 0-100 的值
   * @returns 0-4 的等级
   */
  private convertToFiveLevels(value: number): 0 | 1 | 2 | 3 | 4 {
    // 边界检查
    if (value < 0) return 0
    if (value > 100) return 4

    // 计算等级
    if (value <= 20) return 0
    if (value <= 40) return 1
    if (value <= 60) return 2
    if (value <= 80) return 3
    return 4
  }

  private ohidBaseMixWord(options: {
    write: boolean
    palettes: number[]
    order: OHID_MIX_ORDER
    len: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_base_mix)
      : CMD_OHID.ohid_cmd_base_mix
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum
    view.setUint8(offset++, options.order) // order
    if (options.len > 14) options.len = 14
    for (let i = 0; i < options.len; i++) {
      view.setUint32(offset, options.palettes[i] || 0, true)
      offset += 4
    }

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    const checksum = calculateChecksum(view, dataLength, 4)

    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  private ohidDriverRGBParam(options: {
    write: boolean
    back: number
    palettes: number[]
    gray: number
    mode: number
    speed: number
    sleep: number
    on: number
    on_sleep: number
    reverse: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_driver_rgb_param)
      : CMD_OHID.ohid_cmd_driver_rgb_param
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum

    // 添加 32 位数据
    view.setUint32(offset, options.back, true)
    offset += 4
    for (let i = 0; i < 8; i++) {
      view.setUint32(offset, options.palettes[i] || 0, true)
      offset += 4
    }

    // 添加单字节参数
    view.setUint8(offset++, options.gray)
    view.setUint8(offset++, options.mode)
    view.setUint8(offset++, options.speed)
    view.setUint8(offset++, options.sleep)
    view.setUint8(offset++, options.on)
    view.setUint8(offset++, options.on_sleep)
    view.setUint8(offset++, options.reverse)

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    const checksum = calculateChecksum(view, dataLength, 4)

    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  /**
   * 从 ArrayBuffer 解析 RGB 参数选项
   * 这是 ohidDriverRGBPARAM 函数的逆函数
   *
   * @param buffer - ArrayBuffer 数据包
   * @returns 解析出的 options 参数
   */
  private parseRGBParamOptions(buffer: ArrayBuffer): {
    write: boolean
    back: number
    palettes: number[]
    gray: number
    mode: number
    speed: number
    sleep: number
    on: number
    on_sleep: number
    reverse: number
  } {
    const view = new DataView(buffer)
    let offset = 0

    // 1. 解析头部
    const HEAD = view.getUint8(offset++) // 协议头
    const dataLength = view.getUint8(offset++) // 数据长度
    const cmd = view.getUint8(offset++) // 命令
    const checksum = view.getUint8(offset++) // 校验和

    // 2. 验证协议头
    if (HEAD !== 0xa5) {
      throw new Error(`无效的协议头: 期望 0xa5, 实际 0x${HEAD.toString(16)}`)
    }

    // 3. 验证数据长度
    const expectedLength = 4 + 4 * 8 + 7 // 头部4字节 + 8个调色板*4字节 + 7个单字节参数
    if (dataLength !== expectedLength) {
      throw new Error(`无效的数据长度: 期望 ${expectedLength}, 实际 ${dataLength}`)
    }

    // 4. 解析写入/读取标志
    const isWrite = (cmd & 0x80) !== 0
    const baseCmd = cmd & 0x7f

    // 验证命令
    if (baseCmd !== CMD_OHID.ohid_cmd_driver_rgb_param) {
      throw new Error(
        `无效的命令: 期望 0x${CMD_OHID.ohid_cmd_driver_rgb_param.toString(16)}, 实际 0x${baseCmd.toString(16)}`,
      )
    }

    // 5. 解析 32 位数据
    const back = view.getUint32(offset, true) // 小端序
    offset += 4

    const palettes: number[] = []
    for (let i = 0; i < 8; i++) {
      palettes.push(view.getUint32(offset, true))
      offset += 4
    }

    // 6. 解析单字节参数
    const gray = view.getUint8(offset++)
    const mode = view.getUint8(offset++)
    const speed = view.getUint8(offset++)
    const sleep = view.getUint8(offset++)
    const on = view.getUint8(offset++)
    const on_sleep = view.getUint8(offset++)
    const reverse = view.getUint8(offset)

    // 7. 可选：验证校验和
    const calculatedChecksum = calculateChecksum(view, dataLength, 4)
    if (checksum !== calculatedChecksum) {
      console.warn(
        `校验和不匹配: 期望 0x${checksum.toString(16)}, 计算 0x${calculatedChecksum.toString(16)}`,
      )
    }

    return {
      write: isWrite,
      back,
      palettes,
      gray,
      mode,
      speed,
      sleep,
      on,
      on_sleep,
      reverse,
    }
  }
  // ==================== 公共接口 ====================

  /**
   * 获取所有灯效
   * @param lightingType 灯光类型，默认为 'keyboardLighting'，用于扩展其他灯光类型
   */
  async getEffects(
    target: KeyboardLightingTarget = LIGHTING_TARGET.Keyboard,
  ): Promise<KeyboardLightingEffect[]> {
    // 1.从manifest中获取灯光的默认配置
    const lightingConfig = this.device.manifest.capabilities.find(
      (cap: any) => cap.type === 'lighting',
    ) as LightingCapability | undefined
    if (!lightingConfig) return []
    const effects = lightingConfig.config.effects.filter((effect) => effect.target === target)
    console.log(`[OHID] Getting effects`, effects)

    return effects
  }

  /**
   * 设置背光灯效
   * @param effect - 灯效索引
   */
  async setCurrentEffect(effect: number): Promise<void> {
    const keysColorWithBuffer = await this.getConfig(LightingDomainType.Dynamic, effect)
    if (!keysColorWithBuffer.buffer) return
    const options = this.parseRGBParamOptions(keysColorWithBuffer.buffer.buffer as ArrayBuffer)
    const packetInfo = this.ohidDriverRGBParam({
      write: true,
      back: options.back,
      palettes: options.palettes,
      gray: options.gray,
      mode: effect,
      speed: options.speed,
      sleep: options.sleep,
      on: options.on,
      on_sleep: options.on_sleep,
      reverse: options.reverse,
    })

    const dataView = new DataView(packetInfo)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 创建输出数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )
    const responseBytes = new Uint8Array(
      response.data.buffer,
      response.data.byteOffset,
      response.data.byteLength,
    )

    if (responseBytes[2] == 0xff) {
      throw new Error('OHIDKeyboardLighting setCurrentEffect fail')
    }
  }

  async getCurrentEffect(): Promise<number> {
    console.log(`[OHIDLighting] Getting current effect`)

    const packetInfo = this.ohidDriverRGBParam({
      write: false,
      back: 0,
      palettes: [],
      gray: 0,
      mode: 0,
      speed: 0,
      sleep: 0,
      on: 1,
      on_sleep: 0,
      reverse: 0,
    })

    const dataView = new DataView(packetInfo)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 创建输出数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )

    const responseBytes = new Uint8Array(
      response.data.buffer,
      response.data.byteOffset,
      response.data.byteLength,
    )
    if (response.data.byteLength <= 41) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `input report 长度不足，无法读取当前灯效: ${response.data.byteLength}`,
      )
    }

    const modeID = responseBytes[41]
    console.log(`[OHIDLighting] Current effect modeID: ${modeID}`)
    return modeID
  }

  // ==================== 动态灯效配置 ====================
  /**
   * 设置指定动态颜色效果为指定颜色模式
   * @param rgb 指定颜色
   * @param index 动态灯效索引
   * @returns
   */
  //async setDynamicColorEffect(rgb: RGB, index: number): Promise<void> {
  async setConfig(
    _domain: LightingDomainType,
    _index: number,
    config: Partial<LightingConfigData>,
  ): Promise<void> {
    if (!config.rgb) return
    const rgb = config.rgb
    const colorValue = this.rgbToUint32(rgb)
    const palettes: number[] = []
    palettes.push(colorValue) // 存储 32 位颜色值
    const packetInfo = this.ohidBaseMixWord({
      write: true,
      palettes,
      order: OHID_MIX_ORDER.mix_order_rgb_color,
      len: 1,
    })
    const dataView = new DataView(packetInfo)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 创建输出数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )
    const responseBytes = new Uint8Array(
      response.data.buffer,
      response.data.byteOffset,
      response.data.byteLength,
    )
    if (responseBytes[2] == 0xff) {
      throw new Error('setDynamicColorEffect setCurrentEffect fail')
    }
  }

  /**
   * 获取指定动态颜色效果
   * @param index 动态灯效索引
   * @returns
   */
  //async getCurrentDynamicColorEffect(index: number): Promise<KeysColorWithBuffer> {
  async getConfig(domain: LightingDomainType, index: number): Promise<LightingConfigData> {
    console.log(`[OHIDLighting] Getting current dynamic color effect`)
    const packetInfo = this.ohidDriverRGBParam({
      write: false,
      back: 0,
      palettes: [],
      gray: 0,
      mode: index,
      speed: 0,
      sleep: 0,
      on: 1,
      on_sleep: 0,
      reverse: 0,
    })

    const dataView = new DataView(packetInfo)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 创建输出数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )

    const buffer = new Uint8Array(
      response.data.buffer,
      response.data.byteOffset,
      response.data.byteLength,
    )
    if (response.data.byteLength <= 41) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `input report 长度不足，无法读取当前灯效: ${response.data.byteLength}`,
      )
    }
    const brightness = buffer[40] * 25
    const speed = buffer[42] * 25
    const isColorful = true
    const rgb = {
      r: buffer[10],
      g: buffer[9],
      b: buffer[8],
    }

    return {
      index,
      domainType: domain,
      brightness,
      speed,
      isColorful,
      rgb,
      buffer,
    }
  }

  /**
   * 设置亮度和速度
   * @param domain
   * @param index
   * @param key
   * @param value
   * @returns
   */
  async setProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
    value: any,
  ): Promise<void> {
    const keysColorWithBuffer = await this.getConfig(domain, index)
    if (!keysColorWithBuffer.buffer) return
    const options = this.parseRGBParamOptions(keysColorWithBuffer.buffer.buffer as ArrayBuffer)
    if (key == 'brightness') {
    }
    const packetInfo = this.ohidDriverRGBParam({
      write: true,
      back: options.back,
      palettes: options.palettes,
      gray: key == 'brightness' ? this.convertToFiveLevels(value) : options.gray,
      mode: index,
      speed: key == 'speed' ? this.convertToFiveLevels(value) : options.speed,
      sleep: options.sleep,
      on: options.on,
      on_sleep: options.on_sleep,
      reverse: options.reverse,
    })

    const dataView = new DataView(packetInfo)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 创建输出数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )
    const responseBytes = new Uint8Array(
      response.data.buffer,
      response.data.byteOffset,
      response.data.byteLength,
    )

    if (responseBytes[2] == 0xff) {
      throw new Error('OHIDKeyboardLighting setDynamicColorEffectBrightness fail')
    }
  }

  /**
   * 设置指定动态颜色效果的亮度
   * @param brightness 亮度
   * @param index 动态灯效索引
   * @returns
   */
  // async setDynamicColorEffectBrightness(brightness: number, index: number): Promise<void> {
  //   const keysColorWithBuffer = await this.getCurrentDynamicColorEffect(index)
  //   const options = this.parseRGBParamOptions(keysColorWithBuffer.buffer.buffer as ArrayBuffer)
  //   const packetInfo = this.ohidDriverRGBParam({
  //     write: true,
  //     back: options.back,
  //     palettes: options.palettes,
  //     gray: this.convertToFiveLevels(brightness),
  //     mode: index,
  //     speed: options.speed,
  //     sleep: options.sleep,
  //     on: options.on,
  //     on_sleep: options.on_sleep,
  //     reverse: options.reverse,
  //   })

  //   const dataView = new DataView(packetInfo)
  //   //获取 report ID
  //   const outputReportId = REPORT_ID
  //   const inputReportId = REPORT_ID

  //   // 创建输出数据
  //   const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
  //   const outputData = new Uint8Array(64)
  //   outputData.set(paddedData, 0)
  //   const response = await this.device.sendAndReceive(
  //     outputReportId,
  //     outputData,
  //     inputReportId,
  //     500,
  //   )
  //   const responseBytes = new Uint8Array(
  //     response.data.buffer,
  //     response.data.byteOffset,
  //     response.data.byteLength,
  //   )

  //   if (responseBytes[2] == 0xff) {
  //     throw new Error('OHIDKeyboardLighting setDynamicColorEffectBrightness fail')
  //   }
  // }

  /**
   * 设置指定动态颜色效果的速度
   * @param speed 速度
   * @param index 动态灯效索引
   * @returns
   */
  // async setDynamicColorEffectSpeed(speed: number, index: number): Promise<void> {
  //   const keysColorWithBuffer = await this.getCurrentDynamicColorEffect(index)
  //   const options = this.parseRGBParamOptions(keysColorWithBuffer.buffer.buffer as ArrayBuffer)
  //   const packetInfo = this.ohidDriverRGBParam({
  //     write: true,
  //     back: options.back,
  //     palettes: options.palettes,
  //     gray: options.gray,
  //     mode: index,
  //     speed: this.convertToFiveLevels(speed),
  //     sleep: options.sleep,
  //     on: options.on,
  //     on_sleep: options.on_sleep,
  //     reverse: options.reverse,
  //   })

  //   const dataView = new DataView(packetInfo)
  //   //获取 report ID
  //   const outputReportId = REPORT_ID
  //   const inputReportId = REPORT_ID

  //   // 创建输出数据
  //   const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
  //   const outputData = new Uint8Array(64)
  //   outputData.set(paddedData, 0)
  //   const response = await this.device.sendAndReceive(
  //     outputReportId,
  //     outputData,
  //     inputReportId,
  //     500,
  //   )
  //   const responseBytes = new Uint8Array(
  //     response.data.buffer,
  //     response.data.byteOffset,
  //     response.data.byteLength,
  //   )

  //   if (responseBytes[2] == 0xff) {
  //     throw new Error('OHIDKeyboardLighting setDynamicColorEffectSpeed fail')
  //   }
  // }
}
